import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCamera,
  FiCheckCircle,
  FiMapPin,
  FiCalendar,
} from "react-icons/fi";
import { ImageOff, Camera as CameraLucide } from "lucide-react";
import {
  getMission,
  uploadMissionPhotos,
  saveConvoyingStep,
  listMyMissions,
  typeLabel,
  formatDate,
  assetUrl,
} from "../../utils/missionsApi";
import MissionTypeIcon from "./components/MissionTypeIcon";

const PHASES = [
  { key: "before",    label: "Avant travaux",   icon: "🚗" },
  { key: "during",    label: "En cours",        icon: "🔧" },
  { key: "after",     label: "Après travaux",   icon: "✨" },
];

const CONVOY_PHASES = [
  { key: "departure", label: "Départ",          icon: "🚛" },
  { key: "arrival",   label: "Arrivée",         icon: "🏁" },
];

function PhotoBlock({ title, photos, onAdd, addLabel = "Ajouter des photos" }) {
  const inputRef = useRef(null);
  return (
    <div className="pmm-card" style={{ background: "var(--cream-warm)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{title}</h3>
        <button className="pmm-btn pmm-btn--primary" onClick={() => inputRef.current?.click()}>
          <FiCamera /> {addLabel}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length) onAdd(files);
            e.target.value = "";
          }}
        />
      </div>
      <div className="pmm-grid pmm-grid--4" style={{ marginTop: 10 }}>
        {(photos || []).map((p, i) => (
          <a key={i} href={assetUrl(p.url)} target="_blank" rel="noreferrer">
            <img src={assetUrl(p.url)} alt="" style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 10, border: "1px solid var(--border)" }} />
          </a>
        ))}
        {(photos || []).length === 0 && (
          <div style={{
            gridColumn: "1 / -1", color: "var(--text-muted)", fontSize: 13,
            display: "flex", alignItems: "center", gap: 8, justifyContent: "center",
            padding: "18px 0", background: "#fff", border: "1px dashed var(--border)",
            borderRadius: 12,
          }}>
            <ImageOff size={18} /> Aucune photo — capturez sur place pour valider l'étape.
          </div>
        )}
      </div>
    </div>
  );
}

function SignatureRow({ phase, mission, onSaved }) {
  const [mileage, setMileage] = useState(
    phase === "departure" ? (mission.departureMileage || "")
    : (mission.arrivalMileage || "")
  );
  const [signedBy, setSignedBy] = useState("");

  const save = async () => {
    try {
      const payload = { mileage: mileage ? Number(mileage) : undefined };
      if (signedBy.trim()) {
        payload.signature = {
          signedBy: signedBy.trim(),
          dataUrl: `data:text/plain;base64,${btoa(`Signé par ${signedBy.trim()} le ${new Date().toLocaleString("fr-FR")}`)}`,
        };
      }
      await saveConvoyingStep(mission._id, phase, payload);
      onSaved();
      alert("✅ Étape sauvegardée");
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur");
    }
  };

  return (
    <div className="pmm-card" style={{ background: "#fff" }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 15 }}>
        {phase === "departure" ? "🚛 Confirmation départ" : "🏁 Confirmation arrivée"}
      </h3>
      <div className="pmm-row pmm-row--3">
        <div className="pmm-field">
          <label>Kilométrage</label>
          <input type="number" min={0} value={mileage} onChange={(e) => setMileage(e.target.value)} />
        </div>
        <div className="pmm-field" style={{ gridColumn: "span 2" }}>
          <label>Signature ({phase === "departure" ? "remettant" : "réceptionnaire"})</label>
          <input type="text" placeholder="Nom prénom" value={signedBy} onChange={(e) => setSignedBy(e.target.value)} />
          <span className="pmm-field-hint">La signature dessinée sera capturée par l'app mobile (à venir).</span>
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <button className="pmm-btn pmm-btn--primary" onClick={save}>
          <FiCheckCircle /> Enregistrer l'étape
        </button>
      </div>
    </div>
  );
}

export default function MissionPhotos() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (paramId) {
        const m = await getMission(paramId);
        setMission(m);
      } else {
        const [created, assigned] = await Promise.all([
          listMyMissions("created").catch(() => ({ items: [] })),
          listMyMissions("assigned").catch(() => ({ items: [] })),
        ]);
        const merged = [...(created.items || []), ...(assigned.items || [])]
          .filter((m, idx, arr) => arr.findIndex((x) => x._id === m._id) === idx);
        setList(merged.filter((m) => !["annulee"].includes(m.status)));
      }
    } finally {
      setLoading(false);
    }
  }, [paramId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (phase, files) => {
    try {
      await uploadMissionPhotos(mission._id, files, phase);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur upload");
    }
  };

  if (!paramId) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div className="pmm-topbar">
          <div>
            <h1 className="pmm-h-accent">Photos & validation</h1>
            <div className="pmm-topbar-sub">Choisissez une mission pour gérer ses photos et validations.</div>
          </div>
        </div>
        {loading ? <div className="pmm-spinner" />
          : list.length === 0 ? (
            <div className="pmm-empty">
              <div className="pmm-empty-icon"><CameraLucide size={28} /></div>
              <h3>Pas de mission active</h3>
              <p>Vos missions à photographier apparaîtront ici.</p>
            </div>
          ) : (
            <div className="pmm-grid pmm-grid--2">
              {list.map((m) => (
                <article className="pmm-mission-card" key={m._id}>
                  <div className="pmm-mission-card__head">
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0, flex: 1 }}>
                      <MissionTypeIcon type={m.type} size={38} />
                      <div style={{ minWidth: 0 }}>
                        <h3 className="pmm-mission-card__title">{m.title}</h3>
                        <div className="pmm-mission-card__type">{typeLabel(m.type)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="pmm-mission-card__meta">
                    <span><FiMapPin /> <b>{m.city}</b></span>
                    <span><FiCalendar /> {formatDate(m.date)}</span>
                  </div>
                  <div className="pmm-mission-card__actions">
                    <button className="pmm-btn pmm-btn--primary" onClick={() => navigate(`/missions/${m._id}/photos`)}>
                      <FiCamera /> Gérer les photos
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
      </div>
    );
  }

  if (loading) return <div className="pmm-card"><div className="pmm-spinner" /></div>;
  if (!mission) return (
    <div className="pmm-empty">
      <div className="pmm-empty-icon">❓</div>
      <h3>Mission introuvable</h3>
    </div>
  );

  const isConvoy = mission.type === "convoyage";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="pmm-topbar">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="pmm-btn" onClick={() => navigate("/missions/photos")}><FiArrowLeft /> Retour</button>
          <MissionTypeIcon type={mission.type} size={48} />
          <div>
            <h1>Photos &amp; validation</h1>
            <div className="pmm-topbar-sub">{mission.title}</div>
          </div>
        </div>
      </div>

      {isConvoy ? (
        <>
          {CONVOY_PHASES.map((p) => (
            <PhotoBlock
              key={p.key}
              title={`${p.icon} Photos ${p.label.toLowerCase()}`}
              photos={p.key === "departure" ? mission.departurePhotos : mission.arrivalPhotos}
              onAdd={(files) => handleUpload(p.key, files)}
            />
          ))}
          <SignatureRow phase="departure" mission={mission} onSaved={load} />
          <SignatureRow phase="arrival" mission={mission} onSaved={load} />
        </>
      ) : (
        PHASES.map((p) => (
          <PhotoBlock
            key={p.key}
            title={`${p.icon} ${p.label}`}
            photos={
              p.key === "before" ? mission.beforePhotos
              : p.key === "during" ? mission.duringPhotos
              : mission.afterPhotos
            }
            onAdd={(files) => handleUpload(p.key, files)}
          />
        ))
      )}
    </div>
  );
}
